<template>
  <div v-if="!isMarketWithoutSale" :class="bemBlock">
    <div :class="bemElement('content')">
      <TheProductCustomizerMainSelector
        v-show="mainProducts.length > 1"
        :reset="reset"
      />
      <TheProductCustomizerComplementSelector :reset="reset" />

      <div
        class="cart-and-ipay"
        :class="[bemElement('add-to-cart'), bemElement('actions')]"
      >
        <input type="hidden" class="add-to-cart-url" :value="addToCartUrl" />
        <span
          :class="bemElement('total-price', 'desktop')"
          :style="{ visibility: selectedMainProduct ? 'visible' : 'hidden' }"
        >
          {{ $msg('price.total', 'customizer') + ' ' + totalFormattedPrice }}
        </span>
        <button
          :class="[
            bemElement('add-to-cart-btn', ['desktop']),
            'btn',
            'btn-primary',
            'btn-big',
            'interaction--add-to-bag',
          ]"
          data-interaction="interaction--add-to-bag"
          :data-interaction-state="initialStateAddToBag"
          :disabled="!selectedMainProduct"
          @click="handleAddToCart"
        >
          <BaseSpinner small />
          <span class="default-content">
            {{
              selectedMainProduct
                ? $msg('button.addtocart', 'product')
                : $msg('product.unavailable', 'customizer')
            }}
          </span>
          <span class="swap-content">
            {{ $msg('product.added', 'customizer') }}
          </span>
        </button>
      </div>

      <button
        v-if="!productAddedToCart && isBreakpointMobile && ctaVisible"
        :class="[
          bemElement('add-to-cart-btn', [
            'mobile',
            { 'full-width': isAppRequest },
          ]),
          bemElement('actions'),
          'add-to-cart-mobile',
          'btn',
          'btn--cta',
          'interaction--add-to-bag-mobile',
        ]"
        data-interaction="interaction--add-to-bag-mobile"
        :data-interaction-state="initialStateAddToBagMobile"
        :disabled="!selectedMainProduct"
        @click="handleAddToCart"
      >
        <BaseSpinner small white />
        <span class="default-content">
          <span
            v-if="selectedMainProduct"
            :class="bemElement('total-price', 'mobile')"
          >
            {{ $msg('price.total', 'customizer') + ' ' + totalFormattedPrice }}
          </span>
          <span class="one-size">
            {{
              selectedMainProduct
                ? $msg('button.addtocart', 'product')
                : $msg('product.unavailable', 'customizer')
            }}
          </span>
        </span>

        <span class="swap-content">
          {{ $msg('product.added', 'customizer') }}
        </span>
      </button>

      <BaseModalCTA
        v-if="productAddedToCart && isBreakpointMobile"
        :class="[bemElement('actions'), 'the-product-details__actions']"
        :is-open="true"
        :no-padding="true"
      >
        <p :class="[bemElement('text')]">
          {{ $msg('product.added', 'customizer') }}
        </p>
        <button
          :class="[bemElement('btn-modal', 'btn-link'), 'btn', 'btn--cta']"
        >
          {{ $msg('price.total', 'customizer') + ' ' + totalFormattedPrice }}
        </button>
      </BaseModalCTA>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { getters, types } from '../../store';
import EventBusConfig from '../../config/eventBusConfig';
import bemClassesMixin from '../../mixins/bemClassesMixin';
import createPrice from '../../../common/models/priceModel';
import windowResizeMixin from '../../mixins/windowResizeMixin';
import BaseModalCTA from '../BaseModalCTA.vue';
import BaseSpinner from '../BaseSpinner.vue';
import TheProductCustomizerComplementSelector from './TheProductCustomizerComplementSelector.vue';
import TheProductCustomizerMainSelector from './TheProductCustomizerMainSelector.vue';
import { STATE_ADD_TO_BAG } from '../../../interactions/add-to-bag';
import { STATE_ADD_TO_BAG_MOBILE } from '../../../interactions/add-to-bag-mobile';
import { actionAddToCart } from '../../../product/base';
import getFrontConfig from '../../../common/getFrontConfig';
import {
  getParameterValueFromRequest,
  isWebPlatform,
} from '../../../common/utils/api';
import { addToCart as addToCartAppsCallback } from '../../../components/home/appLib';
import getAnalyticsEventBus from '../../../common/getAnalyticsEventBus';

const analyticsEventBus = getAnalyticsEventBus();

const {
  apiConfig,
  isMarketWithoutSale,
  responsive: { bp_575: bp575 },
} = getFrontConfig();

export default {
  name: 'TheProductCustomizerSelector',

  components: {
    BaseModalCTA,
    BaseSpinner,
    TheProductCustomizerComplementSelector,
    TheProductCustomizerMainSelector,
  },

  mixins: [bemClassesMixin, windowResizeMixin],

  props: {
    ctaVisible: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      dataChangeBreakpoint: bp575,
      isMarketWithoutSale,
      productAddedToCart: false,
      reset: false,
    };
  },

  computed: {
    ...mapGetters({
      customizerId: getters.productCustomizer.customizerId,
      mainProducts: getters.productCustomizer.mainProducts,
      selectedMainProduct: getters.productCustomizer.selectedMainProduct,
      selectedComplementProducts:
        getters.productCustomizer.selectedComplementProducts,
      totalPrice: getters.productCustomizer.totalPrice,
    }),
    ...mapState({
      addToCartUrl: (state) => state.product.details?.addToCartUrl ?? '',
    }),
    initialStateAddToBag() {
      return this.selectedMainProduct
        ? STATE_ADD_TO_BAG.ENABLED
        : STATE_ADD_TO_BAG.DISABLED;
    },
    initialStateAddToBagMobile() {
      return this.selectedMainProduct
        ? STATE_ADD_TO_BAG_MOBILE.ENABLED
        : STATE_ADD_TO_BAG_MOBILE.DISABLED;
    },
    isAppRequest() {
      const platform = getParameterValueFromRequest(
        apiConfig.PLATFORM_PARAMETER_NAME
      );
      return !isWebPlatform(platform);
    },
    totalFormattedPrice() {
      return createPrice(this.totalPrice).formatted;
    },
  },

  mounted() {
    this.$eventBus?.$on(
      EventBusConfig.events.TheProductDetails.addedToCart,
      this.handleProductAddedToCart
    );
  },

  beforeDestroy() {
    this.$eventBus?.$off(
      EventBusConfig.events.TheProductDetails.addedToCart,
      this.handleProductAddedToCart
    );
  },

  methods: {
    handleAddToCart(e) {
      if (this.selectedMainProduct) {
        const selectedProducts = [];
        selectedProducts.push(this.selectedMainProduct);

        // Send event for analytics
        analyticsEventBus.publish(
          analyticsEventBus.EVENTS.productCustomizerAddedToCart,
          {
            customizerId: this.customizerId,
          }
        );

        if (this.selectedComplementProducts?.length) {
          selectedProducts.push(...this.selectedComplementProducts);
        }

        const pidsObj = selectedProducts.map((product) => ({
          pid: product.pid,
          qty: product.qty,
          storeId: product.storeId,
        }));

        if (this.isAppRequest) {
          addToCartAppsCallback(pidsObj);
          setTimeout(() => {
            this.handleProductAddedToCart();
          }, 400);
        } else {
          const params = new URLSearchParams();
          params.append('pidsObj', JSON.stringify(pidsObj));
          actionAddToCart(params, e.target);
        }
      }
    },
    handleProductAddedToCart() {
      if (this.isBreakpointMobile) {
        this.productAddedToCart = true;
        setTimeout(() => {
          this.productAddedToCart = false;
          this.resetCustomizer();
        }, 8000);
      } else {
        setTimeout(() => {
          this.resetCustomizer();
        }, 2000);
      }
    },
    resetCustomizer() {
      this.reset = false;
      this.$store.dispatch(types.productCustomizer.resetCustomizer);
      this.$nextTick(() => {
        this.reset = true;
      });
    },
  },
};
</script>
